import { useCallback, useState } from "react";
import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Check,
  Cloud,
  Loader2,
  Star,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { aggregateSaveStatus, type SaveStatus } from "@/hooks/use-autosave";
import { formatApiError } from "@/lib/format-api-error";
import { useProgram } from "./use-program";
import { useArchiveTierProgram } from "./use-archive-program";
import { useSetDefaultProgram } from "./use-set-default-program";
import { useUpdateTierProgram } from "./use-update-program";
import { TierMatrixEditor } from "./tier-matrix-editor";
import { TierAssignmentsTab } from "./tier-assignments-tab";
import { TierProgramSettingsForm } from "./tier-program-settings-form";

// Phase 7 sub-bucket 12 — tier program editor shell. Three tabs (matrix /
// assignments / settings) keyed by ?tab= URL state, mirroring sub-bucket 11's
// form-builder shell. Loads the program detail via useProgram(id) →
// queryKeys.tiers.program(id); Phase 6 mutation hooks write optimistically
// against the same key.
//
// Settings live in a tab (legacy used a side Sheet from the header); the
// tab approach is deep-linkable and avoids focus-trap interactions with the
// matrix DnD context. The header carries Make-default + Archive/Restore
// quick actions because those flip program-level state regardless of which
// tab the user is in.
//
// Save indicator aggregates SaveStatus across the matrix-tab (row-label
// autosaves) + settings-tab (per-field autosaves). Per-cell on-blur writes
// and per-tier-name inline edits are not represented in the aggregate
// because they resolve synchronously via their own pending state.

type Tab = "matrix" | "assignments" | "settings";

const TABS: ReadonlyArray<{ key: Tab; label: string }> = [
  { key: "matrix", label: "Matrix" },
  { key: "assignments", label: "Assignments" },
  { key: "settings", label: "Settings" },
];

function normalizeTab(raw: string | null): Tab {
  if (raw === "matrix" || raw === "assignments" || raw === "settings") {
    return raw;
  }
  return "matrix";
}

export function TierProgramEditorPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = normalizeTab(searchParams.get("tab"));

  if (!id) return <Navigate to="/admin/tiers" replace />;

  return (
    <TierProgramEditorInner
      programId={id}
      tab={tab}
      onTabChange={(next) => {
        const sp = new URLSearchParams(searchParams);
        sp.set("tab", next);
        setSearchParams(sp, { replace: true });
      }}
    />
  );
}

function TierProgramEditorInner({
  programId,
  tab,
  onTabChange,
}: {
  programId: string;
  tab: Tab;
  onTabChange: (next: Tab) => void;
}) {
  const query = useProgram(programId);
  const [matrixStatus, setMatrixStatus] = useState<SaveStatus>("idle");
  const [settingsStatus, setSettingsStatus] = useState<SaveStatus>("idle");

  const handleMatrixStatus = useCallback(
    (s: SaveStatus) => setMatrixStatus(s),
    [],
  );
  const handleSettingsStatus = useCallback(
    (s: SaveStatus) => setSettingsStatus(s),
    [],
  );

  if (query.isLoading) {
    return <EditorSkeleton />;
  }
  if (query.isError || !query.data) {
    return (
      <EmptyState
        icon={ArrowLeft}
        title="Couldn't load tier program"
        description={
          query.error?.code === "not_found"
            ? "This program has been archived or deleted."
            : (query.error?.message ?? "Try refreshing.")
        }
        action={
          <Link to="/admin/tiers" className="text-sm text-accent hover:underline">
            ← Back to tier programs
          </Link>
        }
      />
    );
  }

  const detail = query.data;
  const aggregateStatus = aggregateSaveStatus(matrixStatus, settingsStatus);

  return (
    <div>
      <EditorHeader
        programId={detail.program.id}
        name={detail.program.name}
        slug={detail.program.slug}
        description={detail.program.description}
        isDefault={detail.program.is_default}
        isArchived={detail.program.is_archived}
        saveStatus={aggregateStatus}
      />

      <TabsNav active={tab} onChange={onTabChange} />

      <div role="tabpanel" className="mt-4">
        {tab === "matrix" && (
          <TierMatrixEditor
            detail={detail}
            onSaveStatusChange={handleMatrixStatus}
          />
        )}
        {tab === "assignments" && (
          <TierAssignmentsTab
            programId={detail.program.id}
            tiers={detail.tiers}
          />
        )}
        {tab === "settings" && (
          <TierProgramSettingsForm
            program={detail.program}
            onSaveStatusChange={handleSettingsStatus}
          />
        )}
      </div>
    </div>
  );
}

function EditorHeader({
  programId,
  name,
  slug,
  description,
  isDefault,
  isArchived,
  saveStatus,
}: {
  programId: string;
  name: string;
  slug: string;
  description: string | null;
  isDefault: boolean;
  isArchived: boolean;
  saveStatus: SaveStatus;
}) {
  const navigate = useNavigate();
  const setDefault = useSetDefaultProgram();
  const archive = useArchiveTierProgram();
  const updateProgram = useUpdateTierProgram();

  const pending =
    setDefault.isPending || archive.isPending || updateProgram.isPending;

  function handleMakeDefault() {
    setDefault.mutate(
      { id: programId },
      {
        onSuccess: () => toast.success(`"${name}" is now the default program`),
        onError: (err) => toast.error(formatApiError(err, "Failed to set default")),
      },
    );
  }

  function handleArchive() {
    archive.mutate(
      { id: programId },
      {
        onSuccess: () => {
          toast.success("Program archived");
          navigate("/admin/tiers");
        },
        onError: (err) => toast.error(formatApiError(err, "Failed to archive")),
      },
    );
  }

  function handleRestore() {
    updateProgram.mutate(
      { id: programId, patch: { is_archived: false } },
      {
        onSuccess: () => toast.success("Program restored"),
        onError: (err) => toast.error(formatApiError(err, "Failed to restore")),
      },
    );
  }

  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <Link
          to="/admin/tiers"
          className="mb-1.5 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          All tier programs
        </Link>
        <h1 className="flex flex-wrap items-center gap-2 font-display text-2xl font-semibold tracking-tight">
          {name}
          {isDefault && (
            <span className="inline-flex items-center gap-1 rounded-full border border-accent/40 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-accent">
              <Star className="h-3 w-3" /> Default
            </span>
          )}
          {isArchived && (
            <span className="inline-flex items-center gap-1 rounded-full border border-border/70 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              <Archive className="h-3 w-3" /> Archived
            </span>
          )}
        </h1>
        {description && (
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            {description}
          </p>
        )}
        <p className="mt-1 font-mono text-xs text-muted-foreground">{slug}</p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <SaveIndicator state={saveStatus} />
        {!isDefault && !isArchived && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleMakeDefault}
            disabled={pending}
          >
            <Star className="mr-1.5 h-3.5 w-3.5" />
            Make default
          </Button>
        )}
        {isArchived ? (
          <Button
            size="sm"
            variant="outline"
            onClick={handleRestore}
            disabled={pending}
          >
            <ArchiveRestore className="mr-1.5 h-3.5 w-3.5" />
            Restore
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={handleArchive}
            disabled={pending}
          >
            <Archive className="mr-1.5 h-3.5 w-3.5" />
            Archive
          </Button>
        )}
      </div>
    </div>
  );
}

function SaveIndicator({ state }: { state: SaveStatus }) {
  if (state === "saving") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        Saving…
      </span>
    );
  }
  if (state === "saved") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-status-active">
        <Check className="h-3 w-3" />
        Saved
      </span>
    );
  }
  if (state === "error") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-destructive">
        Save failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground/70">
      <Cloud className="h-3 w-3" />
      Auto-save on
    </span>
  );
}

function TabsNav({
  active,
  onChange,
}: {
  active: Tab;
  onChange: (next: Tab) => void;
}) {
  return (
    <div className="flex items-center gap-0.5 border-b border-border/70">
      {TABS.map((t) => {
        const isActive = active === t.key;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className={`relative inline-flex h-9 items-center gap-1.5 border-b-2 px-3 text-sm font-medium transition-colors ${
              isActive
                ? "border-accent text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            role="tab"
            aria-selected={isActive}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function EditorSkeleton() {
  return (
    <div>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-3 w-32" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-8 w-28" />
          <Skeleton className="h-8 w-24" />
        </div>
      </div>
      <Skeleton className="h-9 w-full" />
      <Skeleton className="mt-4 h-64 w-full rounded-xl" />
    </div>
  );
}
